/* eslint-disable react-refresh/only-export-components */
import {
  DimeTable,
  DimeTableHeader,
  DimeTableBody,
  DimeTableRow,
  DimeTableHead,
  DimeTableCell,
  DimeChip,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/table')({
  component: TableDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-background">{children}</div>
  );
}

type StatusVariant = 'success' | 'warning' | 'destructive' | 'secondary';

interface PartRow {
  sku: string;
  name: string;
  category: string;
  stock: number;
  status: string;
  statusVariant: StatusVariant;
}

const PARTS: PartRow[] = [
  {
    sku: 'BRK-001',
    name: 'Brake Caliper Assembly',
    category: 'Brakes',
    stock: 48,
    status: 'Active',
    statusVariant: 'success',
  },
  {
    sku: 'ENG-042',
    name: 'Cylinder Head Gasket',
    category: 'Engine',
    stock: 7,
    status: 'Low stock',
    statusVariant: 'warning',
  },
  {
    sku: 'SUS-017',
    name: 'Front Strut Mount',
    category: 'Suspension',
    stock: 0,
    status: 'Out of stock',
    statusVariant: 'destructive',
  },
  {
    sku: 'ELE-203',
    name: 'Alternator 120A',
    category: 'Electrical',
    stock: 22,
    status: 'Discontinued',
    statusVariant: 'secondary',
  },
];

function TableDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Table</h1>
      <p className="mb-8 text-muted-foreground">
        Semantic HTML table primitives with consistent spacing and border styles. Use when column
        definitions are static and you don't need sorting or pagination. For dynamic data, prefer{' '}
        <code className="font-mono text-sm">DimeDataTable</code>.
      </p>

      <DocsSection title="Basic table">
        <Preview>
          <DimeTable>
            <DimeTableHeader>
              <DimeTableRow className="hover:bg-transparent">
                <DimeTableHead>SKU</DimeTableHead>
                <DimeTableHead>Name</DimeTableHead>
                <DimeTableHead>Category</DimeTableHead>
                <DimeTableHead className="text-right">Stock</DimeTableHead>
                <DimeTableHead>Status</DimeTableHead>
              </DimeTableRow>
            </DimeTableHeader>
            <DimeTableBody>
              {PARTS.map((part) => (
                <DimeTableRow key={part.sku}>
                  <DimeTableCell className="font-mono text-xs">{part.sku}</DimeTableCell>
                  <DimeTableCell className="font-medium">{part.name}</DimeTableCell>
                  <DimeTableCell className="text-muted-foreground">{part.category}</DimeTableCell>
                  <DimeTableCell className="text-right tabular-nums">{part.stock}</DimeTableCell>
                  <DimeTableCell>
                    <DimeChip variant={part.statusVariant}>{part.status}</DimeChip>
                  </DimeTableCell>
                </DimeTableRow>
              ))}
            </DimeTableBody>
          </DimeTable>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Compose <code className="font-mono">DimeTable</code> →{' '}
          <code className="font-mono">DimeTableHeader</code> /{' '}
          <code className="font-mono">DimeTableBody</code> →{' '}
          <code className="font-mono">DimeTableRow</code> →{' '}
          <code className="font-mono">DimeTableHead</code> /{' '}
          <code className="font-mono">DimeTableCell</code>. The container handles{' '}
          <code className="font-mono">overflow-x-auto</code> automatically.
        </p>
      </DocsSection>

      <DocsSection title="Empty state">
        <Preview>
          <DimeTable>
            <DimeTableHeader>
              <DimeTableRow className="hover:bg-transparent">
                <DimeTableHead>SKU</DimeTableHead>
                <DimeTableHead>Name</DimeTableHead>
                <DimeTableHead>Category</DimeTableHead>
                <DimeTableHead className="text-right">Stock</DimeTableHead>
              </DimeTableRow>
            </DimeTableHeader>
            <DimeTableBody>
              <DimeTableRow>
                <DimeTableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                  No parts found.
                </DimeTableCell>
              </DimeTableRow>
            </DimeTableBody>
          </DimeTable>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Render a single row spanning all columns with <code className="font-mono">colSpan</code>{' '}
          and <code className="font-mono">h-24 text-center</code> for a consistent empty state.
        </p>
      </DocsSection>
    </div>
  );
}
